import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { useWatchStream } from "./use-watch-stream";

export function usePackingSlipPredictions() {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const [predictions, setPredictions] = useState<FulfillmentGroupData[]>([]);
  const [loadedGroupId, setLoadedGroupId] = useState<string | null>(null);

  const loadPredictions = useHandler(async (currentGroupId: string) => {
    if (loadedGroupId === currentGroupId) {
      return;
    }

    try {
      const response = await rpcClient.getPackingSlipPredictions({
        currentGroupId,
      });

      setPredictions(response.predictions);
      setLoadedGroupId(currentGroupId);
    } catch (error) {
      datadogLogs.logger.error("Failed to load predictions", {
        currentGroupId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  });

  // Watch for updates to all cached prediction groups
  const cachedGroupIds = predictions.map((group) => group._id);
  useWatchStream(
    rpcClient,
    "watchFulfillmentGroups",
    { fulfillmentGroupIds: cachedGroupIds },
    ({ fulfillmentGroups }) => {
      setPredictions((currentPredictions) =>
        currentPredictions.map((prediction) => {
          const updatedGroup = fulfillmentGroups.find(
            (group) => group._id === prediction._id,
          );
          return updatedGroup || prediction;
        }),
      );
    },
  );

  const getGroupsByOrderName = useHandler(
    (orderName: string): FulfillmentGroupData[] => {
      // Match liberally like the backend getFulfillmentGroupsByOrderName:
      // - exact match, with #, case insensitive
      // - must match server-models/outbound-labels/fulfillment-groups/fulfillment-group-read.ts
      const normalizedOrderName = orderName.toLowerCase();
      const orderNameWithHash = `#${orderName}`.toLowerCase();

      return predictions.filter((group) =>
        group.orders.some((order) => {
          const normalizedName = order.name.toLowerCase();
          return (
            normalizedName === normalizedOrderName ||
            normalizedName === orderNameWithHash
          );
        }),
      );
    },
  );

  const getCachedGroup = useHandler(
    (groupId: string): FulfillmentGroupData | undefined => {
      return predictions.find((group) => group._id === groupId);
    },
  );

  return {
    loadPredictions,
    getGroupsByOrderName,
    getCachedGroup,
  };
}
